<script setup>
import { WdsReviewsSummary } from '@wds/components';
import { isString } from '@/lib/validation';

const props = defineProps({
  propertyId: {
    type: Number,
    required: true,
  },
  propertyReviewsSummary: {
    type: String,
    required: false,
    default: '',
  },
});

// ### CONSTANTS
const REVIEW_SUMMARY_FEEDBACK_STORAGE_KEY = 'hw:pdp:reviewSummaryFeedback:v1:';

// ### COMPOSABLES
const { $tracking } = useNuxtApp();
const { t } = useI18n();

// ### DATA
const reviewsSummaryFeedbackGiven = ref(false);

// ### COMPUTED
const hasValidReviewsSummary = computed(() => {
  return isString(props.propertyReviewsSummary) && props.propertyReviewsSummary.trim() !== '';
});
const reviewsSummaryFeedbackKey = computed(() => {
  return `${REVIEW_SUMMARY_FEEDBACK_STORAGE_KEY}${props.propertyId}`;
});
const reviewsSummaryTranslations = computed(() => {
  return {
    tTitle: t('t_REVIEWSSUMMARYTITLE'),
    tWasThisHelpful: t('t_REVIEWSSUMMARYHELPFUL'),
    tThanks: t('t_REVIEWSSUMMARYTHANKS'),
    tInfoAriaLabel: t('t_REVIEWSSUMMARYINFOARIA'),
    tThumbsUpAriaLabel: t('t_REVIEWSSUMMARYTHUMBSUPARIA'),
    tThumbsDownAriaLabel: t('t_REVIEWSSUMMARYTHUMBSDOWNARIA'),
    tDisclaimerText: t('t_REVIEWSSUMMARYDISCLAIMER'),
    tCloseAriaLabel: t('t_CLOSE'),
  };
});

// ### METHODS
function handleReviewsSummaryFeedback(feedback) {
  const feedbackData = JSON.stringify({
    vote: feedback,
    at: new Date().toISOString(),
  });

  const feedbackNamingForTracking = {
    like: 'thumbs up',
    dislike: 'thumbs down',
  };

  try {
    localStorage.setItem(reviewsSummaryFeedbackKey.value, feedbackData);
  } catch (error) {
    console.error('Failed to save review summary feedback on localStorage:', error);
  }

  reviewsSummaryFeedbackGiven.value = true;

  $tracking?.onTrackEvent({
    action: 'AI Review Feedback Clicked',
    ai_feedback_button_clicked: feedbackNamingForTracking[feedback],
  });
}

function handleReviewsSummaryDisclaimerOpen() {
  $tracking?.onTrackEvent({ action: 'AI Review Disclaimer Clicked' });
}

onMounted(() => {
  try {
    const userFeedback = localStorage?.getItem(reviewsSummaryFeedbackKey.value) || null;
    reviewsSummaryFeedbackGiven.value = !!userFeedback;
  } catch (error) {
    console.warn('Failed to read review summary feedback from localStorage:', error);
  }
});
</script>

<template>
  <WdsReviewsSummary
    v-if="hasValidReviewsSummary"
    :text="propertyReviewsSummary"
    :translations="reviewsSummaryTranslations"
    :has-feedback="reviewsSummaryFeedbackGiven"
    :is-modal="true"
    @like="handleReviewsSummaryFeedback('like')"
    @dislike="handleReviewsSummaryFeedback('dislike')"
    @disclaimer-open="handleReviewsSummaryDisclaimerOpen"
  />
</template>
